import React, { useState, useRef, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';

const PhotoGallery = ({ language }) => {
  const [userPhoto, setUserPhoto] = useState(null);
  const [userName, setUserName] = useState('');
  const [photoScale, setPhotoScale] = useState(1);
  const [photoPosition, setPhotoPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isGenerating, setIsGenerating] = useState(false);
  
  const canvasRef = useRef(null);
  const previewRef = useRef(null);
  const photoZoneRef = useRef(null);

  const content = {
    fr: {
      title: "Je serai là",
      subtitle: "Personnalisez votre affiche !",
      uploadButton: "Choisir ma photo",
      namePlaceholder: "Entrez votre nom",
      nameLabel: "Votre nom",
      downloadButton: "Télécharger mon affiche",
      resetButton: "Recommencer",
      instructions: "Ajoutez votre photo et votre nom pour créer votre affiche personnalisée",
      dragInstruction: "Glissez pour positionner • Utilisez +/− pour zoomer",
      noPhoto: "Ajoutez une photo pour commencer"
    },
    en: {
      title: "I'll be there",
      subtitle: "Customize your poster!",
      uploadButton: "Choose my photo",
      namePlaceholder: "Enter your name",
      nameLabel: "Your name",
      downloadButton: "Download my poster",
      resetButton: "Start over",
      instructions: "Add your photo and name to create your personalized poster",
      dragInstruction: "Drag to position • Use +/− to zoom",
      noPhoto: "Add a photo to start"
    }
  };

  const text = content[language];

  // Dimensions - Nouvelle affiche 4724x3543
  const TEMPLATE_WIDTH = 4724;
  const TEMPLATE_HEIGHT = 3543;
  const CIRCLE_DIAMETER = 2072;
  const CIRCLE_X = 29;
  const CIRCLE_Y = 666;
  const CIRCLE_RADIUS = CIRCLE_DIAMETER / 2;
  const CIRCLE_CENTER_X = CIRCLE_X + CIRCLE_RADIUS;
  const CIRCLE_CENTER_Y = CIRCLE_Y + CIRCLE_RADIUS;
  const NAME_X = 179;
  const NAME_Y = 2346;
  const NAME_WIDTH = 1889;
  const NAME_HEIGHT = 220;

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setUserPhoto(reader.result);
        setPhotoScale(1);
        setPhotoPosition({ x: 0, y: 0 });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleMouseDown = (e) => {
    if (!userPhoto) return;
    e.preventDefault();
    setIsDragging(true);
    setDragStart({
      x: e.clientX - photoPosition.x,
      y: e.clientY - photoPosition.y
    });
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    e.preventDefault();
    setPhotoPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  }, [isDragging, dragStart]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleTouchStart = (e) => {
    if (!userPhoto) return;
    e.preventDefault();
    e.stopPropagation();
    const touch = e.touches[0];
    setIsDragging(true);
    setDragStart({
      x: touch.clientX - photoPosition.x,
      y: touch.clientY - photoPosition.y
    });
  };

  const handleTouchMove = useCallback((e) => {
    if (!isDragging) return;
    e.preventDefault();
    e.stopPropagation();
    const touch = e.touches[0];
    setPhotoPosition({
      x: touch.clientX - dragStart.x,
      y: touch.clientY - dragStart.y
    });
  }, [isDragging, dragStart]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleWheel = (e) => {
    if (!userPhoto) return;
    e.preventDefault();
    e.stopPropagation();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setPhotoScale(prev => Math.min(Math.max(0.5, prev + delta), 3));
  };

  useEffect(() => {
    if (isDragging) {
      // Empêcher le scroll de la page pendant le drag
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
      
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
      
      return () => {
        document.body.style.overflow = '';
        document.body.style.touchAction = '';
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  // Empêcher le scroll lors du zoom avec la molette
  useEffect(() => {
    const photoZone = photoZoneRef.current;
    if (!photoZone || !userPhoto) return;

    const handleWheelCapture = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    // Attacher avec passive: false pour pouvoir appeler preventDefault
    photoZone.addEventListener('wheel', handleWheelCapture, { passive: false });

    return () => {
      photoZone.removeEventListener('wheel', handleWheelCapture);
    };
  }, [userPhoto]);

  const generateFinalPoster = () => {
    return new Promise((resolve, reject) => {
      const canvas = canvasRef.current;
      const previewDiv = previewRef.current;
      if (!canvas || !previewDiv) {
        reject(new Error('Canvas or preview not found'));
        return;
      }

      const ctx = canvas.getContext('2d');
      canvas.width = TEMPLATE_WIDTH;
      canvas.height = TEMPLATE_HEIGHT;

      // Calculer le ratio entre le canvas haute résolution et l'aperçu
      const previewWidth = previewDiv.offsetWidth;
      const scaleFactor = TEMPLATE_WIDTH / previewWidth;

      const templateImg = new Image();
      templateImg.crossOrigin = 'anonymous';
      templateImg.src = '/images/affiche-template.png';
      
      templateImg.onload = () => {
        // Dessiner d'abord un fond blanc
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, TEMPLATE_WIDTH, TEMPLATE_HEIGHT);

        if (userPhoto) {
          const userImg = new Image();
          userImg.crossOrigin = 'anonymous';
          userImg.src = userPhoto;
          
          userImg.onload = () => {
            ctx.save();
            // Clip circulaire pour la photo
            ctx.beginPath();
            ctx.arc(CIRCLE_CENTER_X, CIRCLE_CENTER_Y, CIRCLE_RADIUS, 0, Math.PI * 2);
            ctx.closePath();
            ctx.clip();

            // Dans l'aperçu, l'image a une taille de base, puis le scale est appliqué via transform
            // Taille de BASE de l'image (avant scale):
            const baseImgWidth = CIRCLE_DIAMETER;
            const baseImgHeight = (userImg.height / userImg.width) * baseImgWidth;
            
            // Dans l'aperçu, transform: scale() s'applique AUTOUR du centre (transformOrigin: 'center center')
            // Donc on doit d'abord positionner l'image à sa taille de base, puis appliquer le scale
            
            // Convertir les offsets de drag
            const scaledPositionX = photoPosition.x * scaleFactor;
            const scaledPositionY = photoPosition.y * scaleFactor;
            
            // Position de base (centre du conteneur) - AVANT scale
            const baseX = CIRCLE_X + CIRCLE_DIAMETER / 2 - baseImgWidth / 2;
            const baseY = CIRCLE_Y + CIRCLE_DIAMETER / 2 - baseImgHeight / 2;
            
            // Appliquer le scale manuellement en ajustant position et taille
            // Quand on scale depuis le centre, la position change aussi
            const scaledWidth = baseImgWidth * photoScale;
            const scaledHeight = baseImgHeight * photoScale;
            
            // Le scale depuis le centre décale aussi la position
            const scaleOffsetX = (baseImgWidth - scaledWidth) / 2;
            const scaleOffsetY = (baseImgHeight - scaledHeight) / 2;
            
            // Position finale
            const x = baseX + scaleOffsetX + scaledPositionX;
            const y = baseY + scaleOffsetY + scaledPositionY;
            
            ctx.drawImage(userImg, x, y, scaledWidth, scaledHeight);
            ctx.restore();

            // Dessiner le template PAR-DESSUS la photo
            ctx.drawImage(templateImg, 0, 0, TEMPLATE_WIDTH, TEMPLATE_HEIGHT);

            if (userName.trim()) {
              ctx.fillStyle = '#ffffff';
              // Calculer la taille de police proportionnelle à la hauteur de la zone
              const fontSize = NAME_HEIGHT ; 
              ctx.font = `${fontSize}px "Last Dream", cursive, sans-serif`;
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              ctx.fillText(userName.trim(), NAME_X + NAME_WIDTH / 2, NAME_Y + NAME_HEIGHT / 2);
            }

            resolve(canvas);
          };
          userImg.onerror = () => reject(new Error('Erreur chargement photo'));
        } else if (userName.trim()) {
          // Dessiner le template
          ctx.drawImage(templateImg, 0, 0, TEMPLATE_WIDTH, TEMPLATE_HEIGHT);
          
          ctx.fillStyle = '#ffffff';
          const fontSize = NAME_HEIGHT * 0.8;
          ctx.font = `${fontSize}px "Last Dream", cursive, sans-serif`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(userName.trim(), NAME_X + NAME_WIDTH / 2, NAME_Y + NAME_HEIGHT / 2);
          resolve(canvas);
        } else {
          // Dessiner seulement le template
          ctx.drawImage(templateImg, 0, 0, TEMPLATE_WIDTH, TEMPLATE_HEIGHT);
          resolve(canvas);
        }
      };
      templateImg.onerror = () => reject(new Error('Erreur chargement template'));
    });
  };

  const handleDownload = async () => {
    if (!userPhoto && !userName.trim()) {
      alert('Ajoutez au moins une photo ou un nom');
      return;
    }

    setIsGenerating(true);
    try {
      const canvas = await generateFinalPoster();
      
      // Conversion en blob pour meilleure compatibilité iOS
      canvas.toBlob((blob) => {
        if (!blob) {
          alert('Erreur lors de la génération de l\'image');
          setIsGenerating(false);
          return;
        }

        // Détection iOS/Safari
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        
        if (isIOS || isSafari) {
          // Pour iOS/Safari: ouvrir l'image dans un nouvel onglet
          const url = URL.createObjectURL(blob);
          const newWindow = window.open(url, '_blank');
          
          if (newWindow) {
            // Message utilisateur pour iOS
            setTimeout(() => {
              alert(language === 'fr' 
                ? 'Appuyez longuement sur l\'image puis sélectionnez "Enregistrer dans Photos"' 
                : 'Long press the image then select "Save to Photos"');
            }, 500);
          } else {
            // Si le popup est bloqué, essayer de télécharger directement
            const link = document.createElement('a');
            link.href = url;
            link.download = `affiche-charlotte-dipanda-${userName.trim() || 'personnalisee'}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
          
          // Nettoyer l'URL après un délai
          setTimeout(() => URL.revokeObjectURL(url), 60000);
        } else {
          // Pour les autres navigateurs: téléchargement standard
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `affiche-charlotte-dipanda-${userName.trim() || 'personnalisee'}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }
        
        setIsGenerating(false);
      }, 'image/png', 1.0);
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la génération');
      setIsGenerating(false);
    }
  };

  const handleReset = () => {
    setUserPhoto(null);
    setUserName('');
    setPhotoScale(1);
    setPhotoPosition({ x: 0, y: 0 });
  };

  return (
    <div className="max-w-6xl mx-auto relative">
      {/* Glow effect */}
      <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-3xl blur-xl opacity-30"></div>
      
      <div className="relative bg-white rounded-3xl shadow-2xl p-8 md:p-12">
        <div className="text-center mb-10">
          <h2 className="text-4xl md:text-6xl font-black bg-gradient-to-r from-navy via-blue-700 to-purple-700 bg-clip-text text-transparent mb-4">{text.title}</h2>
          <p className="text-xl md:text-2xl text-navy/80 mb-3 font-semibold">{text.subtitle}</p>
          <p className="text-sm text-navy/60">{text.instructions}</p>
        </div>

      <div className="grid md:grid-cols-2 gap-8">
        <div className="space-y-6">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <label className="block text-lg font-bold text-navy mb-3">📸 Photo</label>
            <input type="file" id="photo-upload" accept="image/*" onChange={handlePhotoChange} className="hidden" />
            <motion.label
              htmlFor="photo-upload"
              whileHover={{ scale: 1.02, borderColor: 'rgba(0, 51, 102, 0.6)' }}
              whileTap={{ scale: 0.98 }}
              className="flex flex-col items-center justify-center border-4 border-dashed border-navy/30 rounded-xl p-8 cursor-pointer transition-all duration-300 bg-gradient-to-br from-navy/5 to-blue-50"
            >
              {userPhoto ? (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 200 }}
                  className="text-center"
                >
                  <div className="relative inline-block">
                    <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full blur opacity-50"></div>
                    <img src={userPhoto} alt="Preview" className="relative w-32 h-32 rounded-full object-cover mx-auto mb-3 border-4 border-white shadow-xl" />
                  </div>
                  <span className="text-navy font-bold text-lg"> Changer la photo</span>
                </motion.div>
              ) : (
                <>
                  <motion.svg
                    animate={{ y: [0, -5, 0] }}
                    transition={{ repeat: Infinity, duration: 2 }}
                    className="w-16 h-16 text-navy/50 mb-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </motion.svg>
                  <span className="text-navy font-bold text-lg">{text.uploadButton}</span>
                </>
              )}
            </motion.label>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
          >
            <label className="block text-lg font-bold text-black mb-3">✍️ {text.nameLabel}</label>
            <motion.input
              type="text"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              placeholder={text.namePlaceholder}
              maxLength={30}
              whileFocus={{ scale: 1.02 }}
              className="w-full px-5 py-4 border-3 border-navy/30 rounded-xl focus:border-navy focus:outline-none text-lg font-semibold shadow-md focus:shadow-xl transition-all duration-300 bg-white"
            />
          </motion.div>

          {userPhoto && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              className="relative"
            >
              <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-400 to-purple-400 rounded-xl blur opacity-30"></div>
              <div className="relative bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-blue-200 rounded-xl p-5 shadow-lg">
                <p className="text-sm text-navy text-center font-bold mb-3">💡 {text.dragInstruction}</p>
                <div className="flex justify-center items-center gap-4">
                  {/* Bouton Zoom - */}
                  <motion.button
                    onClick={() => setPhotoScale(prev => Math.max(0.5, prev - 0.1))}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="w-10 h-10 rounded-full bg-white border-2 border-navy/30 flex items-center justify-center text-navy font-bold text-xl hover:bg-navy hover:text-white transition-all duration-200 shadow-md"
                  >
                    −
                  </motion.button>
                  
                  <span className="text-xs text-navy/80 font-semibold bg-white px-4 py-2 rounded-full shadow-sm min-w-[80px] text-center">
                    Zoom: {photoScale.toFixed(1)}x
                  </span>
                  
                  {/* Bouton Zoom + */}
                  <motion.button
                    onClick={() => setPhotoScale(prev => Math.min(3, prev + 0.1))}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="w-10 h-10 rounded-full bg-white border-2 border-navy/30 flex items-center justify-center text-navy font-bold text-xl hover:bg-navy hover:text-white transition-all duration-200 shadow-md"
                  >
                    +
                  </motion.button>
                  
                  <motion.span
                    animate={{ opacity: [1, 0.5, 1] }}
                    transition={{ repeat: Infinity, duration: 2 }}
                    className="text-2xl"
                  >
                    🎯
                  </motion.span>
                </div>
              </div>
            </motion.div>
          )}

          <div className="space-y-3 pt-4">
            <motion.button
              onClick={handleDownload}
              disabled={isGenerating || (!userPhoto && !userName.trim())}
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
              className="w-full relative group"
            >
              {/* Button glow */}
              <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-xl blur opacity-60 group-hover:opacity-100 transition duration-300"></div>
              
              <div className="relative bg-gradient-to-r from-navy via-blue-700 to-purple-700 text-white px-6 py-5 rounded-xl font-black text-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-xl overflow-hidden">
                {isGenerating ? (
                  <>
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                    </svg>
                    <span>Génération...</span>
                  </>
                ) : (
                  <>
                    <span className="text-2xl">⬇️</span>
                    <span>{text.downloadButton}</span>
                  </>
                )}
                {/* Shine effect */}
                <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/30 to-transparent"></div>
              </div>
            </motion.button>
            
            <motion.button
              onClick={handleReset}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full bg-gradient-to-r from-gray-100 to-gray-200 text-navy px-6 py-4 rounded-xl font-bold text-lg hover:from-gray-200 hover:to-gray-300 transition-all duration-300 shadow-md hover:shadow-lg"
            >
              🔄 {text.resetButton}
            </motion.button>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <label className="block text-lg font-bold text-navy mb-3">👁️ Aperçu</label>
          <div className="relative group">
            <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-2xl blur-lg opacity-30 group-hover:opacity-50 transition duration-500"></div>
            <div ref={previewRef} className="relative border-4 border-navy/30 rounded-2xl bg-gradient-to-br from-gray-50 to-gray-100 shadow-2xl" style={{ aspectRatio: '4724/3543' }}>
            
            {userPhoto && (
              <div 
                ref={photoZoneRef}
                className="absolute overflow-hidden cursor-move select-none touch-none" 
                style={{ 
                  left: `${(CIRCLE_X / TEMPLATE_WIDTH) * 100}%`, 
                  top: `${(CIRCLE_Y / TEMPLATE_HEIGHT) * 100}%`, 
                  width: `${(CIRCLE_DIAMETER / TEMPLATE_WIDTH) * 100}%`, 
                  height: `${(CIRCLE_DIAMETER / TEMPLATE_HEIGHT) * 100}%`,
                  borderRadius: '50%',
                  zIndex: 1
                }} 
                onMouseDown={handleMouseDown} 
                onTouchStart={handleTouchStart} 
                onWheel={handleWheel}
              >
                <div className="relative w-full h-full">
                  <img 
                    src={userPhoto} 
                    alt="User" 
                    className="pointer-events-none absolute" 
                    draggable="false" 
                    style={{ 
                      transform: `translate(calc(-50% + ${photoPosition.x}px), calc(-50% + ${photoPosition.y}px)) scale(${photoScale})`, 
                      transformOrigin: 'center center',
                      left: '50%',
                      top: '50%',
                      width: '100%',
                      height: 'auto',
                      transition: isDragging ? 'none' : 'transform 0.1s ease-out' 
                    }} 
                  />
                </div>
              </div>
            )}
            
            <img src="/images/affiche-template.png" alt="Template" className="absolute inset-0 w-full h-full object-contain pointer-events-none" draggable="false" style={{ zIndex: 2 }} />

            {userName.trim() && (
              <div className="absolute flex items-center justify-center pointer-events-none" style={{ left: `${(NAME_X / TEMPLATE_WIDTH) * 100}%`, top: `${(NAME_Y / TEMPLATE_HEIGHT) * 100}%`, width: `${(NAME_WIDTH / TEMPLATE_WIDTH) * 100}%`, height: `${(NAME_HEIGHT / TEMPLATE_HEIGHT) * 100}%`, zIndex: 3 }}>
                <p className="text-center" style={{ fontFamily: '"Last Dream", cursive, sans-serif', fontSize: 'clamp(8px, 2.5vw, 24px)', color: '#ffffff', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {userName.trim()}
                </p>
              </div>
            )}

            {!userPhoto && !userName && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center text-navy/40">
                  <svg className="w-16 h-16 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p>{text.noPhoto}</p>
                </div>
              </div>
            )}
            </div>
          </div>
        </motion.div>
      </div>

        <canvas ref={canvasRef} className="hidden" />
      </div>
    </div>
  );
};

export default PhotoGallery;